import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, Sparkle, CalendarBlank, User } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * D.FIL.5 — Modal Valider + Attribuer (web)
 *
 * Permet à l'archi (ou superviseur) de valider une proposition IA `a_valider`
 * et de l'attribuer en un seul geste :
 *   1. PUT /elements/{eid}      → met à jour titre/description/priorité/échéance/assignation/lot
 *   2. PUT /elements/{eid}/etat → passe à 'actif'
 *
 * CHA-08 — listes chargées depuis GET /chantiers/{cid}/equipe et /lots
 * (backend/routes/taches_attribution_routes.py). Le lot est enregistré dans le
 * champ existant `lot` (nom du lot) et l'entreprise dans `entreprise` (nom) +
 * `entreprise_user_ids` ; `lot_id` n'existe pas dans ElementUpdate (ignoré).
 *
 * Pré-remplissage IA : tous les champs IA sont éditables.
 *
 * Props :
 *   open, onClose, chantierId, element (la proposition a_valider), onValidated(eid)
 */

const TYPE_OPTIONS = [
  { value: 'reserve', label: 'Réserve' },
  { value: 'tache', label: 'Action' },
  { value: 'incident', label: 'Incident' },
  { value: 'non_conformite', label: 'Non-conformité' },
  { value: 'observation', label: 'Observation' },
  { value: 'demande_client', label: 'Demande client' },
  { value: 'question_entreprise', label: 'Question entreprise' },
  { value: 'validation', label: 'Validation' },
  { value: 'decision', label: 'Décision' },
  { value: 'info', label: 'Info' },
];

const PRIORITE_OPTIONS = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'normal', label: 'Normal' },
  { value: 'basse', label: 'Basse' },
];

export default function TaskValidateModal({
  open,
  onClose,
  chantierId,
  element,
  onValidated,
}) {
  // Form state
  const [titre, setTitre] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('tache');
  const [priorite, setPriorite] = useState('normal');
  const [echeance, setEcheance] = useState('');
  const [entrepriseUserId, setEntrepriseUserId] = useState('');
  const [lotNom, setLotNom] = useState('');
  // Data
  const [equipe, setEquipe] = useState([]);
  const [lots, setLots] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Pré-remplir depuis l'élément à l'ouverture
  useEffect(() => {
    if (!element || !open) return;
    setTitre(element.titre || '');
    setDescription(element.description || element.description_ia || element.texte_brut || '');
    setType(element.type || 'tache');
    setPriorite(element.priorite || 'normal');
    setEcheance(element.echeance ? String(element.echeance).slice(0, 10) : '');
    const suggested = Array.isArray(element.entreprise_user_ids) && element.entreprise_user_ids.length > 0
      ? String(element.entreprise_user_ids[0])
      : '';
    setEntrepriseUserId(suggested);
    setLotNom(element.lot || '');
    setError('');
  }, [element, open]);

  // Charger l'équipe + les lots à l'ouverture
  useEffect(() => {
    if (!open || !chantierId) return;
    (async () => {
      try {
        const [eq, lt] = await Promise.all([
          axios.get(`${API_URL}/api/chantiers/${chantierId}/equipe`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/chantiers/${chantierId}/lots`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setEquipe(Array.isArray(eq.data) ? eq.data : []);
        setLots(Array.isArray(lt.data) ? lt.data : []);
      } catch {
        /* ignore */
      }
    })();
  }, [open, chantierId]);

  // Filtre équipe pour ne garder que les entreprises
  const entreprises = useMemo(() => {
    return equipe.filter((m) => {
      const role = m.role_projet || m.role || '';
      return role === 'entreprise' || role === 'employe_entreprise';
    });
  }, [equipe]);

  // Le lot déjà saisi peut être un code (terrain) ou un libellé : on le ramène au nom.
  useEffect(() => {
    if (!lotNom || lots.length === 0) return;
    const hit = lots.find((l) => l.nom === lotNom || l.code === lotNom || l.libelle === lotNom);
    if (hit && hit.nom && hit.nom !== lotNom) setLotNom(hit.nom);
  }, [lots, lotNom]);

  const selectEntreprise = (uid) => {
    setEntrepriseUserId(uid);
    // Un seul lot pour cette entreprise : proposé si aucun lot choisi
    const ent = entreprises.find((m) => String(m.user_id) === uid);
    if (!lotNom && ent && Array.isArray(ent.lots) && ent.lots.length === 1) {
      setLotNom(ent.lots[0].nom || ent.lots[0].code || '');
    }
  };

  const handleSubmit = async () => {
    if (!element) return;
    if (!titre.trim()) {
      setError('Le titre est requis.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const eid = element._id || element.id;

      // 1. PUT element (titre, description, priorité, échéance, entreprise, lot, type)
      const ent = entreprises.find((m) => String(m.user_id) === entrepriseUserId);
      const lotChoisi = lots.find((l) => l.nom === lotNom);
      const entrepriseNom = (ent && (ent.entreprise_nom || ent.name))
        || (!entrepriseUserId && lotChoisi && lotChoisi.entreprise_nom) || '';
      const payload = {
        titre: titre.trim(),
        description: description.trim(),
        type,
        priorite,
        echeance: echeance || null,
        entreprise_user_ids: entrepriseUserId ? [entrepriseUserId] : [],
        lot: lotNom,
      };
      if (entrepriseNom) payload.entreprise = entrepriseNom;
      await axios.put(
        `${API_URL}/api/chantiers/${chantierId}/elements/${eid}`,
        payload,
        { withCredentials: true },
      );

      // 2. PUT etat = actif
      await axios.put(
        `${API_URL}/api/chantiers/${chantierId}/elements/${eid}/etat`,
        { etat: 'actif' },
        { withCredentials: true },
      );

      toast.success('Action validée et attribuée');
      onValidated?.(eid);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Erreur lors de la validation';
      setError(detail);
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (!element) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="task-validate-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
            <Sparkle size={18} weight="fill" className="text-[#C4773B]" />
            Valider la proposition IA
          </DialogTitle>
          <p className="text-xs text-[#9A9A9A] mt-1">
            Les champs sont pré-remplis par l'IA, ajustez et attribuez.
          </p>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {/* Titre */}
          <div>
            <Label className="text-xs">Titre *</Label>
            <Input
              value={titre}
              onChange={(e) => setTitre(e.target.value)}
              className="rounded-none"
              data-testid="task-validate-titre"
              placeholder="Titre court de l'action"
            />
          </div>

          {/* Description */}
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="rounded-none min-h-[80px]"
              data-testid="task-validate-description"
              placeholder="Détails de l'action"
            />
          </div>

          {/* Type + Priorité */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="rounded-none" data-testid="task-validate-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Priorité</Label>
              <Select value={priorite} onValueChange={setPriorite}>
                <SelectTrigger className="rounded-none" data-testid="task-validate-priorite">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Échéance + Lot */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs flex items-center gap-1">
                <CalendarBlank size={12} /> Échéance
              </Label>
              <Input
                type="date"
                value={echeance}
                onChange={(e) => setEcheance(e.target.value)}
                className="rounded-none"
                data-testid="task-validate-echeance"
              />
            </div>
            <div>
              <Label className="text-xs">Lot</Label>
              <Select value={lotNom || '__none__'} onValueChange={(v) => setLotNom(v === '__none__' ? '' : v)}>
                <SelectTrigger className="rounded-none" data-testid="task-validate-lot">
                  <SelectValue placeholder="Aucun" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Aucun</SelectItem>
                  {lots.filter((l) => l.nom || l.code).map((l) => (
                    <SelectItem key={l.id} value={l.nom || l.code}>{l.libelle || l.nom || l.code}</SelectItem>
                  ))}
                  {lotNom && !lots.some((l) => (l.nom || l.code) === lotNom) && (
                    <SelectItem value={lotNom}>{lotNom}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Entreprise assignée */}
          <div>
            <Label className="text-xs flex items-center gap-1">
              <User size={12} /> Entreprise assignée
            </Label>
            <Select
              value={entrepriseUserId || '__none__'}
              onValueChange={(v) => selectEntreprise(v === '__none__' ? '' : v)}
            >
              <SelectTrigger className="rounded-none" data-testid="task-validate-entreprise">
                <SelectValue placeholder="Aucune" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Aucune assignation</SelectItem>
                {entreprises.map((m) => {
                  const uid = m.user_id || m._id || m.id;
                  const nomEnt = m.entreprise_nom || m.name || m.email || '(sans nom)';
                  const lotsTxt = (m.lots || []).map((l) => l.code || l.nom).filter(Boolean).join(', ');
                  return (
                    <SelectItem key={uid} value={String(uid)}>
                      {nomEnt}{m.entreprise_nom && m.name && m.name !== m.entreprise_nom ? ` — ${m.name}` : ''}{lotsTxt ? ` (${lotsTxt})` : ''}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {entrepriseUserId && Array.isArray(element.entreprise_user_ids)
              && element.entreprise_user_ids[0] === entrepriseUserId && (
              <p className="text-[10px] text-[#C4773B] mt-1 flex items-center gap-1">
                <Sparkle size={10} weight="fill" /> Suggestion IA
              </p>
            )}
          </div>

          {error && (
            <div className="bg-[#FEF2F2] border-l-4 border-[#DC2626] text-[#991B1B] text-xs p-2.5">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-[#E0E0DB] mt-4">
          <Button
            variant="outline"
            onClick={onClose}
            className="rounded-none"
            disabled={submitting}
            data-testid="task-validate-cancel"
          >
            <X size={14} className="mr-1" /> Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !titre.trim()}
            className="bg-[#16A34A] hover:bg-[#15803D] text-white rounded-none"
            data-testid="task-validate-submit"
          >
            <Check size={14} className="mr-1" />
            {submitting ? 'Validation…' : 'Valider + Attribuer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
